import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useModalContext } from '../../../../context/Modal';
import { formatCurrency, formatNumber } from '../../../../helpers/functions';
import { Button } from '../../../UI';
import { DatePicker } from '../../../form';
import { Input } from '../../../form';
import React from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_ASSET_ALERT } from '../../../../http/apollo-queries';
import { IAssetAlert } from '../../../../types/model';
import { FooterNavigator } from '../../../Footer/FooterNavigator';

interface EditAlertProps {
  smartAlert: IAssetAlert;
}

const EditAlert: React.FC<EditAlertProps> = ({ smartAlert }) => {
  const { t } = useTranslation();
  const { closeModal } = useModalContext();

  const [updateAssetAlert, { loading }] = useMutation(UPDATE_ASSET_ALERT);

  const { asset } = smartAlert;

  const formik = useFormik({
    initialValues: {
      assetName: `${asset.name} - ${asset.symbol}`,
      alertName: smartAlert.name,
      currency: asset.currency,
      priceThreshold: smartAlert.value,
      expireAt: new Date(smartAlert.expireAt),
    },
    onSubmit: ({ expireAt, alertName, priceThreshold }) => {
      return updateAssetAlert({
        variables: {
          alertId: smartAlert.id,
          expireAt,
          name: alertName,
          value: Number(priceThreshold),
        },
      }).finally(() => closeModal());
    },
  });

  const isInvalidForm = !formik.values.priceThreshold;

  return (
    <FormikProvider value={formik}>
      <form
        className={'flex flex-col gap-12 py-10 px-20'}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div>
          <Input
            size="full"
            name="assetName"
            id="assetName"
            formik={formik}
            type="text"
            placeholder={t('alerts.editAlertModal.label.assetName')}
            label={t('alerts.editAlertModal.label.assetName')}
            readOnly={true}
          />
        </div>

        <div className="flex justify-between gap-5">
          <Input
            size="full"
            formik={formik}
            name="alertName"
            id="alertName"
            type="text"
            label={t('alerts.editAlertModal.label.alertName')}
            placeholder={t('alerts.editAlertModal.label.alertName')}
          />

          <DatePicker
            size="full"
            id={'alertDate'}
            date={formik.values.expireAt}
            onChange={(date) => formik.setFieldValue('expireAt', date)}
          />
        </div>

        <div className="flex gap-5">
          <Input
            size="medium"
            name="currency"
            id="currency"
            formik={formik}
            type="text"
            isDisabled={true}
            placeholder={t('alerts.addAlert.currency')}
            label={t('alerts.addAlert.currency')}
            value={asset.currency}
          />

          <Input
            size="medium"
            name="priceThreshold"
            id="priceThreshold"
            formik={formik}
            type="number"
            placeholder={t('alerts.addAlert.priceThreshold')}
            label={t('alerts.addAlert.priceThreshold')}
            validate={(val) => {
              const error: any = {};
              if (!val) {
                error.invalid = true;
              }
              return Object.keys(error).length !== 0 ? error : null;
            }}
          />
        </div>

        {/* footer */}
        <FooterNavigator
          loading={loading}
          isDisabledSaveButton={loading || isInvalidForm}
          saveButtonName="Save"
          onCancel={closeModal}
          onSave={formik.handleSubmit}
        />
      </form>
    </FormikProvider>
  );
};

export default EditAlert;
